import { isProduction, isStaging, UprightEnvironment } from '@upright/common/environment';
import { getServerBaseUrl } from './server-base-url';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { ApiVariantKey } from '@upright/common/model/output/scores/model-entity-data/api-types';
import { isNil, omitBy } from 'lodash';
import { stringify } from 'querystring';
import { urlJoin as urljoin } from '@upright/common/utils/url-management';
import { ModelEntityType } from '../../common/model/input/types';
import { FinalImpactScoreType } from '../../common/model/output/scores/types';
import {
  CompareEntitySet,
  CompareVersionsViewMode,
  ComparisonKey,
} from '../../common/model/output/scores/version-comparison/types';
import { Valence } from '../../common/model/types';
import { removeNullAndUndefined } from '../../common/utils';
import { useGetVariantKey } from '../api/common/hooks';
import { DrilldownQueryParams } from '../common/generic/components/view-templates/bou-drilldown-view/types';
import { joinQueryParams } from '../common/generic/hooks/url-handling/query-params';
import { OrganizationViewMode } from '../views/internal/organizations/single-organization/shared';
import { UserGroupViewMode } from '../views/internal/user-groups/single-user-group/types';
import { CompanyGroupInternalViewMode } from '../views/model-entity-views/company-group-view/internal/types';
import { InternalProductViewMode } from '../views/model-entity-views/product-view/internal/types';
import { ViewMode } from '../views/model-entity-views/shared/view-modes';
import { UserViewViewMode } from '../views/user-view/types';

/**
 * Resolve URL for public API doc
 *
 * @returns URL for API documentation
 */
export function getApiDocUrl() {
  if (isProduction()) {
    // Should be used only to display API url for user, not to be used by frontend.
    return 'https://api.uprightproject.com';
  }
  if (isStaging()) {
    // Should be used only to display API url for user, not to be used by frontend.
    return 'https://staging-api.uprightproject.com';
  }
  const baseUrl = getServerBaseUrl();

  return `${baseUrl}/documentation`;
}

/**
 * Resolve the MCP server URL a customer pastes into their own AI client
 * (Claude, ChatGPT, Microsoft Copilot, …) to connect it to Upright.
 *
 * Display-only, like `getApiDocUrl` above: the frontend never calls this
 * endpoint itself — the external client does, over OAuth 2.1 + PKCE with
 * dynamic client registration, so there is no key or token to hand out
 * alongside it.
 *
 * @returns URL of the MCP server for the current environment
 */
export function getMcpServerUrl() {
  if (isProduction()) {
    return 'https://api.uprightproject.com/api/mcp';
  }
  if (isStaging()) {
    return 'https://staging-api.uprightproject.com/api/mcp';
  }
  return `${getServerBaseUrl()}/api/mcp`;
}

/**
 * If you already know the ModelEntityType, don't use this generic function
 * but use on of the functions below it.
 */
export function getModelEntityUrl(
  type: ModelEntityType,
  id: string,
  name: string | undefined | null,
  lensType?: ImpactLensType,
) {
  const namePart = name ? escapeURLcharactersFromName(name) + '/' || '' : '';
  const lensTypePart = lensType ? `?lens=${lensType}` : '';
  switch (type) {
    case ModelEntityType.COMPANY:
      return `/company/${id}/${namePart}${lensTypePart}`;
    case ModelEntityType.PORTFOLIO:
      return `/portfolio/${id}/${namePart}${lensTypePart}`;
    case ModelEntityType.GROUP:
      return `/company-group/${id}/${namePart}${lensTypePart}`;
    case ModelEntityType.PRODUCT:
    case ModelEntityType.MIXIN:
    case ModelEntityType.PHENOMENON:
      return `/product/${id}/${namePart}`;
  }
}

export function getProductViewUrl(
  productCode: string,
  productName: string | undefined,
  modelEntityType?: ModelEntityType,
) {
  const type = modelEntityType || ModelEntityType.PRODUCT;
  return getModelEntityUrl(type, productCode, productName);
}

export function getCompanyViewUrl(id: string, name: string | undefined, lensType?: ImpactLensType) {
  return getModelEntityUrl(ModelEntityType.COMPANY, id, name, lensType);
}

// TODO OA: handle deeper paths somehow
export function getCompanyViewSubpageUrl(id: string, name: string | undefined, subPage: string) {
  return urljoin(getCompanyViewUrl(id, name), subPage);
}

/**
 * Company-scoped DMA dashboard home — the dashboard side's counterpart to
 * `getCompanyViewUrl`.
 *
 * `name` is required in practice even though the signature allows it to be
 * missing: the route is declared as `company/:id/:name/dashboard/*`, so
 * without a name segment the `dashboard` segment is captured as `:name` and
 * the URL lands on the database-side company view instead. Callers that may
 * not have resolved the name yet should wait rather than pass `undefined`.
 *
 * The org-scoped `/dashboard` is deliberately not built here: it resolves the
 * company from the viewer's own organisation, and a team user visiting it is
 * redirected away (see `dma-v2-view/index.tsx`).
 */
export function getCompanyDashboardUrl(id: string, name: string | undefined) {
  return urljoin(getModelEntityUrl(ModelEntityType.COMPANY, id, name), 'dashboard');
}

export function getCompanyGroupViewUrl(id: string, name: string | undefined, lensType?: ImpactLensType) {
  return getModelEntityUrl(ModelEntityType.GROUP, id, name, lensType);
}

export function getVariantUrl(variantId: string) {
  return `/internal/company-variants/${variantId}`;
}

export function getVariantFreezeUrl(freezeId: string) {
  return `/internal/company-variant-freezes/${freezeId}`;
}

export function getPortfolioViewUrl(id: string, name: string | undefined, lensType?: ImpactLensType) {
  return getModelEntityUrl(ModelEntityType.PORTFOLIO, id, name, lensType);
}

export function useModelEntityDrilldownUrl() {
  const variantKey = useGetVariantKey();
  return (params: ModelEntityDrilldownParams) => {
    if (params.type === ModelEntityType.COMPANY) {
      const queryParams = omitBy({ ...variantKey, ...params.queryParams }, isNil);
      return getModelEntityDrilldownUrl({ ...params, type: ModelEntityType.COMPANY, queryParams });
    } else {
      return getModelEntityDrilldownUrl(params);
    }
  };
}

interface ModelEntityDrilldownParams {
  type: ModelEntityType;
  id: string;
  impactCode?: string;
  valence?: Valence;
  name?: string;
  resultId?: number | string;
  scoreType?: FinalImpactScoreType;
  queryParams?: DrilldownQueryParams & ApiVariantKey;
}
export function getModelEntityDrilldownUrl(params: ModelEntityDrilldownParams) {
  const { impactCode, resultId, type, id, name, queryParams } = params;
  const scoreType = params.scoreType ?? FinalImpactScoreType.CENTS_PER_DOLLAR;
  const valence = params.valence ?? Valence.POSITIVE;
  const entityUrl = getModelEntityUrl(type, id, name);
  if (!impactCode || !resultId) {
    throw Error(`Missing necessary parameters for drilldown url!`);
  }
  return urljoin(
    entityUrl,
    `${ViewMode.DRILLDOWN}/${resultId}/${impactCode}/${valence}/${scoreType}${joinQueryParams(queryParams)}`,
  );
}

export enum InternalCompanyViewMode {
  BASIC = 'basic',
  VARIANTS = 'variants',
  PRODUCTS = 'products',
  REVENUE = 'revenue',
  EMPLOYEES = 'employees',
  INCOME = 'income',
  FINANCIAL_STATEMENTS = 'financial-statements',
  PROBLEMS = 'problems',
  ACCESS = 'access',
  COMPARISONS = 'comparisons',
  BLANKET = 'blanket',
  PAI_ASSESSMENTS = 'pai-assessments',
  PAI_ESTIMATES = 'pai-estimates',
  EU_ASSESSMENTS = 'eu-assessments',
  EVENTS = 'events',
  CSRD_COMPANY_LEVEL_INPUTS = 'csrd-company-level-inputs',
  CSRD_ON_DEMAND_VIEW = 'csrd-on-demand',
  CSRD_REASONINGS_VIEW = 'csrd-reasonings',
  CSRD_TAILORING_CONFIG = 'csrd-tailoring-config',
  EVIDENCE_COVERAGE = 'evidence-coverage',
  // Appended rather than placed beside FINANCIAL_STATEMENTS: the sidebar takes both its order and
  // its Ctrl+ hotkeys from enum position, so inserting mid-list would move every later tab's
  // shortcut.
  SITE_LOCATIONS = 'site-locations',
}

export function getModelEntityInternalUrl(type: ModelEntityType, id: string, name: string | undefined) {
  switch (type) {
    case ModelEntityType.PRODUCT:
    case ModelEntityType.PHENOMENON:
      return getProductViewInternalUrl(id, type, name, InternalProductViewMode.BASIC);
    case ModelEntityType.COMPANY:
      return getCompanyInternalUrl(id, name, InternalCompanyViewMode.BASIC);
    case ModelEntityType.MIXIN:
    case ModelEntityType.GROUP:
    case ModelEntityType.PORTFOLIO:
    default:
      return urljoin(getModelEntityUrl(type, id, name), 'internal');
  }
}

export function getCompanyInternalUrl(id: string, name: string | undefined, viewMode: InternalCompanyViewMode) {
  return urljoin(getModelEntityUrl(ModelEntityType.COMPANY, id, name), 'internal', viewMode);
}

export function getCompanyGroupInternalUrl(
  id: string,
  name: string | undefined,
  viewMode: CompanyGroupInternalViewMode,
) {
  return urljoin(getModelEntityUrl(ModelEntityType.GROUP, id, name), 'internal', viewMode);
}

export function getUserViewUrl(username: string | undefined, viewMode?: UserViewViewMode) {
  if (!username) return '';
  return `/user/${username}${viewMode ? `/${viewMode}` : ''}`;
}

export function getProductViewInternalUrl(
  id: string,
  type: ModelEntityType,
  name: string | undefined,
  viewMode: InternalProductViewMode,
) {
  return urljoin(getModelEntityUrl(type, id, name), 'internal', viewMode);
}

function escapeURLcharactersFromName(input?: string) {
  if (!input) {
    return '';
  }

  /**
   * OA: When using <Link>, React Router automatically decodes %-signs. If a % sign is encoded
   * as %25, it will be decoded back to % when being passed to the "to" prop of a <Link>,
   * and will break stuff when being used in a URL.
   *
   * Apparently it's a bug with the history package https://github.com/remix-run/react-router/issues/5824
   *
   * Fix: encode the %-sign one extra time so it gets decoded to only %. A downside of this is that when we use this
   * function to generate URLs for other than React Router use, the percentage signs will be encoded to %2525. This is likely
   * not a big issue, as these names are only used for initial population of fields while waiting for better data from the server.
   *
   * A better fix for a later time would be to have a custom <Link> component that handles this for the react router case.
   */
  const ret = encodeURIComponent(input.replaceAll('%', encodeURIComponent('%')));
  return ret;
}

export function getUserGroupModeUrl(id: number, name: string, viewMode: UserGroupViewMode) {
  return `/internal/user-groups/${id}/${escapeURLcharactersFromName(name)}/${viewMode}`;
}

export function getOrganizationModeUrl(id: number, name: string, viewMode: OrganizationViewMode) {
  return `/internal/organization/${id}/${escapeURLcharactersFromName(name)}/${viewMode}`;
}

// eslint-disable-next-line upright/too-many-arguments
export function getCompareVersionsUrl(
  versionA: string,
  versionB: string,
  entitySet: CompareEntitySet,
  viewMode: CompareVersionsViewMode,
  comparison: ComparisonKey,
  entityId: string | undefined,
) {
  const query = stringify(
    removeNullAndUndefined({
      ...comparison,
      entityId,
    }),
  );
  return `/internal/versions/compare/${versionA}/${versionB}/${entitySet}/${viewMode}/?${query}`;
}

export function getConstraintSetUrl(id: number) {
  return `/internal/constraint-set/${id}`;
}

export function getMixinTemplateUrl(template: string) {
  return `/internal/mixin-template/${template}`;
}

/**
 * Generate URLs to point to the platform from external sites.
 *
 * @param platformPath Needs to start with a leading slash.
 */
export function getPlatformUrl(platformPath: string, env = UprightEnvironment.AWS_PRODUCTION) {
  let baseUrl = 'https://uprightplatform.com';
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (env) {
    case UprightEnvironment.AWS_STAGING:
      baseUrl = 'https://staging.uprightplatform.com';
      break;
    default:
    case UprightEnvironment.AWS_PRODUCTION:
      baseUrl = 'https://uprightplatform.com';
      break;
    case UprightEnvironment.TEST:
      baseUrl = 'http://host.docker.internal';
      break;
    case UprightEnvironment.DEVELOPMENT:
      baseUrl = 'http://localhost:3333';
      break;
    case UprightEnvironment.AWS_PLAYGROUND:
      baseUrl = 'https://playground.development.uprightplatform.io';
      break;
  }

  return `${baseUrl}${platformPath}`;
}
